<template>
  <base-page-template>
    <v-row
        justify="center"
        class="pt-10"
        v-if="dataSampleLoading">
      <v-col cols="12">
        <v-progress-circular indeterminate/>
      </v-col>
      <v-col cols="12">
        読み込み中
      </v-col>
    </v-row>
    <el-table
        v-else
        :data="body">
      <el-table-column
          v-for="(val, key) in headers"
          :key="key"
          :prop="`${key}.value`"
          :label="val">
      </el-table-column>

      <el-table-column
          v-if="!readonly"
          key="operations"
          width="96"
          align="right">
        <template #default="{row}">
          <icon-btns>
            <icon-btn
                type="delete"
                @click="openDeleteDialog(row)">
            </icon-btn>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <v-col v-if="!readonly" cols="12">
      <el-tooltip placement="right" :open-delay="200">
        <template #content>
          サンプルレコードの取得リクエストを送信します。
        </template>
        <template #default>
          <v-btn large color="primary" @click="requestAutoFetch" class="mt-5">
            リクエスト
            <v-icon color="white" class="ml-1" small>mdi-help-circle</v-icon>
          </v-btn>
        </template>
      </el-tooltip>
    </v-col>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete.visible"
        title-text="サンプルレコード削除"
        title-color="delete"
        apply-text="削除"
        header-text="サンプルレコードを削除します。本当によろしいですか？"
        @close="closeDeleteDialog"
        @apply="deleteDataSample">
    </simple-dialog>
  </base-page-template>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import Swal from "sweetalert2";

export default {
  name: 'DataImplementationSample',
  components: {
    IconBtn,
    IconBtns,
    BasePageTemplate,
    SimpleDialog
  },
  inject: {
    dialog: {default: null},
  },
  props: {
    dataImplementationId: {
      type: String,
      required: true
    },
    loadImmediately: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      dialogDelete: {
        visible: false,
        form: {}
      },
      dataSample: null,
      dataSampleLoading: false,
    }
  },
  created() {
    if(this.loadImmediately) {
      this.load();
    } else {
      this.$watch(
          _ => [this.dataImplementationId, this.dialog?.visible],
          _ => {
            if (this.dialog === null || this.dialog.visible) {
              this.load();
            }
          },
          {immediate: true}
      );
    }
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client'
    }),
    headers() {
      return this.dataSample?.headers ?? {};
    },
    body() {
      return this.dataSample?.body ?? [];
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
      hideSnackbar: 'hideSnackbar'
    }),
    async load() {
      try {
        this.dataSampleLoading = true;
        this.dataSample = null;
        const {data: dataSample} = await this.hcsClient.get(`/data_implementations/${this.dataImplementationId}/data_sample`);
        this.dataSample = dataSample;
        this.$emit('load', dataSample);
      } finally {
        this.dataSampleLoading = false;
      }
    },
    openDeleteDialog(row) {
      this.dialogDelete = {
        visible: true,
        form: row
      }
    },
    closeDeleteDialog() {
      this.dialogDelete.visible = false;
    },
    async deleteDataSample() {
      this.showSnackbar({
        message: 'サンプルレコードを削除中...',
        timeout: false,
        loading: true
      });

      try {
        for (const {id} of Object.values(this.dialogDelete.form)) {
          await this.hcsClient.delete(`/data_sample_values/${id}`);
        }

        this.$store.commit({
          type: 'showSnackbar',
          message: `サンプルレコードが削除されました`
        });
      } catch (e) {
        console.error(e);
        this.$store.commit({
          type: 'showSnackbar',
          color: 'failed',
          message: `サンプルレコードの削除に失敗しました`
        });
      } finally {
        await this.load();
      }
    },
    async requestAutoFetch() {
      const snackbarTimer = setTimeout(() => {
        this.showSnackbar({
          message: '自動取得リクエストを送信中...',
          timeout: false,
          loading: true
        });
      }, 500);

      if(this.dialog) {
        this.dialog.close();
      }

      try {
        await this.hcsClient.post(`/data_implementations/${this.dataImplementationId}/request_sample_data`);

        clearTimeout(snackbarTimer);
        this.hideSnackbar();
        await Swal.fire({
          title: '自動取得リクエスト送信',
          html: 'サンプルデータの自動取得リクエストを送信しました。<br>' +
              'しばらく時間をおいて再度サンプルデータを確認してください。',
          icon: 'info',
          showCloseButton: true,
          showCancelButton: false,
          focusConfirm: true,
          confirmButtonText: "OK",
        });
      } catch (e) {
        console.error(e);
        clearTimeout(snackbarTimer);
        this.$store.commit({
          type: 'showSnackbar',
          color: 'failed',
          message: `自動取得リクエストの送信に失敗しました`
        });
      }
    }
  },
}
</script>
